import React from 'react';
import { motion } from 'framer-motion';
import * as Icons from 'lucide-react';

const KPICard = ({ kpi, index = 0 }) => {
  // Safety check for undefined kpi
  if (!kpi) {
    return null;
  }
  
  const IconComponent = Icons[kpi.icon] || Icons.Activity;
  
  const getChangeColor = () => {
    if (!kpi.change_type) return 'text-gray-600';
    switch (kpi.change_type) {
      case 'increase':
        return 'text-green-600';
      case 'decrease':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl transition-all duration-300 hover:scale-[1.03] cursor-pointer relative overflow-hidden"
      whileHover={{ y: -5 }}
    >
      {/* Background gradient accent */}
      <div 
        className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
        style={{ 
          background: `radial-gradient(circle, ${kpi.color} 0%, transparent 70%)`,
          transform: 'translate(20%, -20%)'
        }}
      />
      
      <div className="relative z-10">
        {/* Icon at top */}
        <div className="flex justify-center mb-4">
          <div 
            className="p-4 rounded-2xl shadow-lg"
            style={{ 
              backgroundColor: `${kpi.color}15`,
              boxShadow: `0 4px 15px ${kpi.color}30`
            }}
          >
            <IconComponent 
              className="w-7 h-7" 
              style={{ color: kpi.color }}
            />
          </div>
        </div>
        
        {/* Label */}
        <p className="text-center text-sm font-medium text-gray-500 mb-2">
          {kpi.label}
        </p>
        
        {/* Value */}
        <h3 className="text-center text-4xl font-bold text-gray-900 mb-2">
          {kpi.value}
        </h3>
        
        {/* Description or change indicator */}
        {kpi.description && (
          <p className="text-center text-sm text-gray-400 mb-1">
            {kpi.description}
          </p>
        )}
        
        {/* Count display */}
        {kpi.count !== undefined && kpi.count > 0 && (
          <div className="text-center mb-2">
            <span className="text-2xl font-semibold text-gray-700">
              {kpi.count.toLocaleString()}
            </span>
          </div>
        )}
        
        {/* Details */}
        {kpi.details && (
          <p className="text-center text-xs text-gray-500 font-medium">
            {kpi.details}
          </p>
        )}
        
        {kpi.change && (
          <div className="flex justify-center items-center mt-3">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
              kpi.change_type === 'increase' ? 'bg-green-100 text-green-700' :
              kpi.change_type === 'decrease' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {kpi.change > 0 ? '↑' : kpi.change < 0 ? '↓' : '→'} {Math.abs(kpi.change)}%
              <span className="ml-1 opacity-75">vs last month</span>
            </div>
          </div>
        )}
      </div>
      
      {/* Bottom accent line */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-1"
        style={{ 
          background: `linear-gradient(90deg, ${kpi.color}20, ${kpi.color}, ${kpi.color}20)`
        }}
      />
    </motion.div>
  );
};

export default KPICard;
